import momentTimezone from "moment-timezone";
import moment from "moment";
import React from "react";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import "dayjs/locale/ko";
import "dayjs/locale/zh-cn";
import "dayjs/locale/ja";
import "dayjs/locale/en";
import IntlMessage from "components/util-components/IntlMessage";
import { regionData, regionDataBeta } from "constants/SharedConstant";
import i18n from "i18next";
import { TRANSLATION_MUTATION, WRITE_MUTATION, SPELL_CHECK_MUTATION } from "graphql/mutation/translation";
import { client as apolloClient } from "App"; // App.js에서 export된 Apollo Client 인스턴스
import { FaFile, FaFilePdf, FaFileWord, FaFileExcel, FaFilePowerpoint, FaFileVideo, FaFileAudio, FaFileArchive, FaFileCode} from 'react-icons/fa';
import { RiFileHwpLine } from "react-icons/ri";

dayjs.extend(utc);
dayjs.extend(timezone);

class Utils {
  /**
   * Get first character from first & last sentences of a username
   * @param {String} name - Username
   * @return {String} 2 characters string
   */
  static getNameInitial(name) {
    let initials = name.match(/\b\w/g) || [];
    return ((initials.shift() || "") + (initials.pop() || "")).toUpperCase();
  }

  /**
   * Check if file is an image based on extension
   * @param {String} filename - File name with extension
   * @return {Boolean} true if file is an image
   */
  static isImageFile = (filename) => {
    return /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(filename);
  };

  /**
   * Get file icon based on file extension
   * @param {String} filename - File name with extension
   * @return {JSX.Element} React icon component
   */
  static getFileIcon = (filename) => {
    if(!filename) {
      return <FaFile style={{ fontSize: '24px', marginRight: '8px' }} />;
    }
    const ext = filename?.toLowerCase().split('.').pop();
    switch(ext) {
      case 'pdf':
        return <FaFilePdf style={{ fontSize: '24px', marginRight: '8px' }} />;
      case 'doc':
      case 'docx':
        return <FaFileWord style={{ fontSize: '24px', marginRight: '8px' }} />;
      case 'xls':
      case 'xlsx':
        return <FaFileExcel style={{ fontSize: '24px', marginRight: '8px' }} />;
      case 'ppt':
      case 'pptx':
        return <FaFilePowerpoint style={{ fontSize: '24px', marginRight: '8px' }} />;
      case 'mp4':
      case 'avi':
      case 'mov':
        return <FaFileVideo style={{ fontSize: '24px', marginRight: '8px' }} />;
      case 'mp3':
      case 'wav':
        return <FaFileAudio style={{ fontSize: '24px', marginRight: '8px' }} />;
      case 'zip':
      case 'rar':
      case '7z':
        return <FaFileArchive style={{ fontSize: '24px', marginRight: '8px' }} />;
      case 'js':
      case 'jsx':
      case 'ts':
      case 'tsx':
      case 'html':
      case 'css':
        return <FaFileCode style={{ fontSize: '24px', marginRight: '8px' }} />;
      case 'hwp':
      case 'hwpx':
        return <RiFileHwpLine style={{ fontSize: '24px', marginRight: '8px' }} />;
      default:
        return <FaFile style={{ fontSize: '24px', marginRight: '8px' }} />;
    }
  };

  /**
   * Get current path related object from Navigation Tree
   * @param {Array} navTree - Navigation Tree from directory 'configs/NavigationConfig'
   * @param {String} path - Location path you looking for e.g '/app/dashboards/analytic'
   * @return {Object} object that contained the path string
   */
  static getRouteInfo(navTree, path) {
    // 전체 네비게이션 배열 순회
    const findRouteInfoRecursive = (items, currentPath) => {
      if (!items || !Array.isArray(items)) return null;
      
      // 직접 경로 매칭 시도
      let foundItem = items.find(item => item.path === currentPath);
      if (foundItem) return foundItem;
      
      // 하위 메뉴 검색
      for (const item of items) {
        if (item.submenu && item.submenu.length > 0) {
          const result = findRouteInfoRecursive(item.submenu, currentPath);
          if (result) return result;
        }
      }
      
      return null;
    };
    
    // 경로에서 쿼리 파라미터 제거
    const cleanPath = path?.split('?')[0];
    if (!cleanPath) return null;
    
    // 먼저 직접 탐색
    if (Array.isArray(navTree)) {
      const result = findRouteInfoRecursive(navTree, cleanPath);
      if (result) return result;
    } else if (navTree.path === cleanPath) {
      return navTree;
    }
    
    // 서브메뉴 순회 탐색
    if (navTree.submenu) {
      for (let i = 0; i < navTree.submenu.length; i++) {
        const route = this.getRouteInfo(navTree.submenu[i], cleanPath);
        if (route) return route;
      }
    }
    
    // 저장된 메뉴 키를 사용하여 검색
    const selectedMenuKey = localStorage.getItem('selected_menu_key');
    if (selectedMenuKey) {
      // 메뉴 키로 해당 메뉴 항목 찾기
      const findItemByKey = (items, targetKey) => {
        if (!items || !Array.isArray(items)) return null;
        
        let foundItem = items.find(item => item.key === targetKey);
        if (foundItem) return foundItem;
        
        for (const item of items) {
          if (item.submenu && item.submenu.length > 0) {
            const result = findItemByKey(item.submenu, targetKey);
            if (result) return result;
          }
        }
        
        return null;
      };
      
      if (Array.isArray(navTree)) {
        const keyResult = findItemByKey(navTree, selectedMenuKey);
        if (keyResult) return keyResult;
      }
    }
    
    return null;
  }

  /**
   * Get accessible color contrast
   * @param {String} hex - Hex color code e.g '#3e82f7'
   * @return {String} 'dark' or 'light'
   */
  static getColorContrast(hex) {
    if (!hex) {
      return "dark";
    }
    const threshold = 130;
    const hRed = hexToR(hex);
    const hGreen = hexToG(hex);
    const hBlue = hexToB(hex);
    function hexToR(h) {
      return parseInt(cutHex(h).substring(0, 2), 16);
    }
    function hexToG(h) {
      return parseInt(cutHex(h).substring(2, 4), 16);
    }
    function hexToB(h) {
      return parseInt(cutHex(h).substring(4, 6), 16);
    }
    function cutHex(h) {
      return h.charAt(0) === "#" ? h.substring(1, 7) : h;
    }
    const cBrightness = (hRed * 299 + hGreen * 587 + hBlue * 114) / 1000;
    if (cBrightness > threshold) {
      return "dark";
    } else {
      return "light";
    }
  }

  /**
   * Darken or lighten a hex color
   * @param {String} color - Hex color code e.g '#3e82f7'
   * @param {Number} percent - Percentage -100 to 100, positive for lighten, negative for darken
   * @return {String} Darken or lighten color
   */
  static shadeColor(color, percent) {
    let R = parseInt(color.substring(1, 3), 16);
    let G = parseInt(color.substring(3, 5), 16);
    let B = parseInt(color.substring(5, 7), 16);
    R = parseInt((R * (100 + percent)) / 100);
    G = parseInt((G * (100 + percent)) / 100);
    B = parseInt((B * (100 + percent)) / 100);
    R = R < 255 ? R : 255;
    G = G < 255 ? G : 255;
    B = B < 255 ? B : 255;
    const RR =
      R.toString(16).length === 1 ? `0${R.toString(16)}` : R.toString(16);
    const GG =
      G.toString(16).length === 1 ? `0${G.toString(16)}` : G.toString(16);
    const BB =
      B.toString(16).length === 1 ? `0${B.toString(16)}` : B.toString(16);
    return `#${RR}${GG}${BB}`;
  }

  /**
   * Convert RGBA to HEX
   * @param {String} rgba - RGBA color code e.g 'rgba(197, 200, 198, .2)')'
   * @return {String} HEX color
   */
  static rgbaToHex(rgba) {
    const trim = (str) => str.replace(/^\s+|\s+$/gm, "");
    const inParts = rgba.substring(rgba.indexOf("("))?.split(","),
      r = parseInt(trim(inParts[0].substring(1)), 10),
      g = parseInt(trim(inParts[1]), 10),
      b = parseInt(trim(inParts[2]), 10),
      a = parseFloat(
        trim(inParts[3].substring(0, inParts[3].length - 1))
      ).toFixed(2);
    const outParts = [
      r.toString(16),
      g.toString(16),
      b.toString(16),
      Math.round(a * 255)
        .toString(16)
        .substring(0, 2),
    ];

    outParts.forEach(function (part, i) {
      if (part.length === 1) {
        outParts[i] = "0" + part;
      }
    });
    return `#${outParts.join("")}`;
  }

  /**
   * Returns either a positive or negative
   * @param {Number} number - number value
   * @param {any} positive - value that return when positive
   * @param {any} negative - value that return when negative
   * @return {any} positive or negative value based on param
   */
  static getSignNum(number, positive, negative) {
    if (number > 0) {
      return positive;
    }
    if (number < 0) {
      return negative;
    }
    return null;
  }

  /**
   * Returns either ascending or descending value
   * @param {Object} a - antd Table sorter param a
   * @param {Object} b - antd Table sorter param b
   * @param {String} key - object key for compare
   * @return {any} a value minus b value
   */
  static antdTableSorter(a, b, key) {
    if (typeof a[key] === "number" && typeof b[key] === "number") {
      return a[key] - b[key];
    }

    if (typeof a[key] === "string" && typeof b[key] === "string") {
      a = a[key].toLowerCase();
      b = b[key].toLowerCase();
      return a > b ? -1 : b > a ? 1 : 0;
    }
    return;
  }

  /**
   * Filter array of object
   * @param {Array} list - array of objects that need to filter
   * @param {String} key - object key target
   * @param {any} value  - value that excluded from filter
   * @return {Array} a value minus b value
   */
  static filterArray(list, key, value) {
    let data = list;
    if (list) {
      data = list.filter((item) => item[key] === value);
    }
    return data;
  }

  /**
   * Remove object from array by value
   * @param {Array} list - array of objects
   * @param {String} key - object key target
   * @param {any} value  - target value
   * @return {Array} Array that removed target object
   */
  static deleteArrayRow(list, key, value) {
    let data = list;
    if (list) {
      data = list.filter((item) => item[key] !== value);
    }
    return data;
  }

  /**
   * Wild card search on all property of the object
   * @param {Number | String} input - any value to search
   * @param {Array} list - array for search
   * @return {Array} array of object contained keyword
   */
  static wildCardSearch(list, input) {
    const searchText = (item) => {
      for (let key in item) {
        if (item[key] == null) {
          continue;
        }
        if (
          item[key]
            .toString()
            .toUpperCase()
            .indexOf(input.toString().toUpperCase()) !== -1
        ) {
          return true;
        }
      }
    };
    list = list.filter((value) => searchText(value));
    return list;
  }

  /**
   * Get Breakpoint
   * @param {Object} screens - Grid.useBreakpoint() from antd
   * @return {Array} array of breakpoint size
   */
  static getBreakPoint(screens) {
    let breakpoints = [];
    for (const key in screens) {
      if (screens.hasOwnProperty(key)) {
        const element = screens[key];
        if (element) {
          breakpoints.push(key);
        }
      }
    }
    return breakpoints;
  }

  static getTimezone = () => {
    const result = [];

    momentTimezone.tz.names().map((item) =>
      result.push({
        desc: `(GMT ${moment.tz(item).format("Z")}) ${item}`,
        country: item,
        offset: moment.tz(item).format("Z"),
      })
    );

    // offsetTmz = offsetTmz.sort((a, b) => a.offset.toString().localeCompare(b.offset.toString()));

    return result;
  };

  static decodeId(id) {
    if (!id || id === "") return "";

    try {
      const decoded = window.atob(id);
      return decoded.split(":")[1];
    } catch (error) {
      // If decoding fails, return the original ID
      return id;
    }
  }

  static validateURL = (errorMessage, _, value) => {
    // Regular expression for URL validation
    const urlRegex =
      /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/;
    if (!value || urlRegex.test(value)) {
      return Promise.resolve();
    }
    return Promise.reject(new Error(errorMessage));
  };

  static removeKey(obj, keyToRemove = "__typename") {
    if (obj === null || typeof obj !== "object") return obj;

    if (Array.isArray(obj)) {
      return obj.map((item) => Utils.removeKey(item, keyToRemove));
    }

    const newObj = {};
    for (const key in obj) {
      if (key !== keyToRemove) {
        newObj[key] = Utils.removeKey(obj[key], keyToRemove);
      }
    }
    return newObj;
  }

  static regexPassword =
    /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).[a-zA-Z0-9#?!@$%^&*-]{7,}$/;

  static uploadImage = async (blobInfo) => {
    const token = localStorage.getItem("auth_token");
    const formData = new FormData();
    formData.append("file", blobInfo.blob());
    formData.append("projectId", this.getProjectId());

    try {
        const response = await fetch(
            `${process.env.REACT_APP_API_URL}/uploadcs/`,
            {
                method: "POST",
                body: formData,
                headers: {
                    authorization: token ? `Bearer ${token}` : "",
                    projectId: this.getProjectId(),
                    type: "cs",
                    "Accept-Language": this.getLocale(),
                },
            }
        );

        if (!response.ok) {
            throw new Error("Network response was not ok");
        }

        const result = await response.json();

        if (result.status) {
            const imageUrl = `${result.cdn}${result.path}`;
            return imageUrl; // 성공 시 이미지 URL 반환
        } else {    
          // 서버에서 내려주는 에러 메시지 사용, 없으면 기본 메시지
          const errorMessage = result.message || "file upload failed";
          throw new Error(errorMessage); // 실패 시 에러 발생
        }
    } catch (error) {
        console.error("file upload failed", error);
        // 네트워크 에러나 다른 에러의 경우 기본 메시지 사용
        throw new Error(error.message || "file upload failed"); // 실패 시 에러 발생
    }
  };

  static handleModalTitle = ({ modalType }) => {
    switch (modalType) {
      case "add":
        return <IntlMessage id="Add" />;
      case "edit":
        return <IntlMessage id="Edit" />;
      case "view":
        return <IntlMessage id="View" />;
      default:
        return <IntlMessage id="View" />;
    }
  };

  static handleModalOkText = ({ modalType }) => {
    switch (modalType) {
      case "add":
        return <IntlMessage id="Add" />;
      case "edit":
        return <IntlMessage id="Save" />;
      case "view":
        return <IntlMessage id="OK" />;
      default:
        return <IntlMessage id="OK" />;
    }
  };

  static getRegionData(regionNo) {
    let objData;

    if (process.env.REACT_APP_BETA === "true") {
      objData = regionDataBeta[String(regionNo)];
    } else {
      objData = regionData[String(regionNo)];
    }

    if (!objData) {
      return regionData["1"];
    }

    return objData;
  }

  // used in policies and terms
  static getWebAccessAddress = ({ project, id, projectId }) => {
    const bucket = project?.ncp?.objectstorage_bucket;
    const ncpId = project?.ncp_project_id;
    let cdn = project?.cdn;

    const baseUrl = process.env.REACT_APP_APPS_URL || 'https://gamepot.apigw.ntruss.com/gpapps/v1';
    return `${baseUrl}/pages/${id}?projectId=` + projectId; 
  };

  static createMenuItems = (submenu, currentPath, renderComponent) => {
    return submenu.map((item) => {
      const pathKey = item.path?.split("/").pop();
      return {
        label: React.createElement(IntlMessage, { id: item.title }),
        key: pathKey,
        children: currentPath === pathKey ? renderComponent(pathKey) : null,
      };
    });
  };

  static findSubmenu = (data, targetKey, currentPath, renderComponent) => {
    for (let item of data) {
      if (item.key === targetKey && item.submenu) {
        return this.createMenuItems(item.submenu, currentPath, renderComponent);
      }
      if (item.submenu) {
        const result = this.findSubmenu(
          item.submenu,
          targetKey,
          currentPath,
          renderComponent
        );
        if (result) return result;
      }
    }
    return null;
  };

  static convertObjectTo2DArray = (obj, index) => {
    return Object.keys(obj).reduce((acc, key) => {
      if (key.startsWith("day_")) {
        const dayNumber = parseInt(key.split("_")[1], 10);
        acc.push([dayNumber === 30 ? 15 : dayNumber, index, obj[key]]);
      }
      return acc;
    }, []);
  };

  static getColor = ({ elm, record }) => {
    const max = 100;
    const ratio = parseFloat(((elm / record[1]) * 100).toFixed(2)) / max;

    // Define RGB values for yellow and red
    const yellow = { r: 255, g: 255, b: 0 };
    const red = { r: 255, g: 0, b: 0 };

    // Interpolate between yellow and red
    const r = Math.round(red.r * ratio + yellow.r * (1 - ratio));
    const g = Math.round(red.g * ratio + yellow.g * (1 - ratio));
    const b = Math.round(red.b * ratio + yellow.b * (1 - ratio));

    return `rgb(${r}, ${g}, ${b})`;
  };

  // this is used for a timezone date picker
  static cellRender = (current, info, timezone = null) => {
    if (typeof current === "object") {
      current = timezone ? current.tz(timezone) : current.tz();
    }
    if (info.type !== "date") {
      return info.originNode;
    }
    if (typeof current === "number") {
      return <div className="ant-picker-cell-inner">{current}</div>;
    }
    return (
      <div
        className="ant-picker-cell-inner"
        title={current.format("YYYY/MM/DD")}
      >
        {typeof current === "object" ? current.date() : current}
      </div>
    );
  };

  static disablePastDate = (current) =>
    current && current < dayjs().tz().startOf("day");

  static disableFutureDate = (current) => current > dayjs().tz().startOf("day");

  static getProjectId() {
    const url = window.location.href; // Get the current URL
    const parsedUrl = new URL(url);
    const pathSegments = parsedUrl.pathname.split("/").filter(segment => segment); // 빈 세그먼트 제거
    
    // 로컬스토리지에 저장된 프로젝트 ID 확인
    const savedProjectId = localStorage.getItem("projectId");
    
    // URL에서 projectId 추출 (companyId 다음 세그먼트)
    let projectIdFromUrl = '';
    
    // URL 형태에 따라 projectId 추출 로직
    if (pathSegments.length >= 2) {
      projectIdFromUrl = pathSegments[1];
      
      // 'auth'가 projectId로 인식되는 경우 방지
      if (projectIdFromUrl === 'auth') {
        projectIdFromUrl = '';
      }
    }
    
    // URL에서 가져온 ID가 비어있는 경우 저장된 ID 사용
    if (!projectIdFromUrl) {
      // 저장된 ID가 'auth'인 경우는 빈 문자열 반환
      if (savedProjectId && savedProjectId !== 'auth') {
        return savedProjectId;
      }
      return '';
    }
    
    // 새로운 projectId를 localStorage에 저장
    if (projectIdFromUrl !== savedProjectId) {
      localStorage.setItem("projectId", projectIdFromUrl);
    }
    
    return projectIdFromUrl;
  }

  static getCompanyId() {
    const url = window.location.href; // Get the current URL
    const parsedUrl = new URL(url);
    const pathSegments = parsedUrl.pathname.split("/").filter(segment => segment); // 빈 세그먼트 제거
    
    // 로컬스토리지에 저장된 회사 ID 확인
    const savedCompanyId = localStorage.getItem("companyId");
    
    // URL에서 companyId 추출
    let companyIdFromUrl = '';
    
    // URL 형태에 따라 companyId 추출 로직 
    // 1. /:companyId/auth/login 형태인 경우
    if (pathSegments.length >= 2 && pathSegments[1] === 'auth' && pathSegments[0] !== 'auth') {
      companyIdFromUrl = pathSegments[0];
    } 
    // 2. /auth/login 형태인 경우는 companyId가 없음
    else if (pathSegments.length >= 1 && pathSegments[0] === 'auth') {
      companyIdFromUrl = '';
    }
    // 3. /:companyId/:projectId/something 형태인 경우
    else if (pathSegments.length >= 2) {
      companyIdFromUrl = pathSegments[0];
    }
    
    // URL에서 가져온 ID가 비어있지 않은 경우, URL의 값이 우선
    if (companyIdFromUrl && companyIdFromUrl !== 'auth') {
      // 로컬스토리지와 다른 경우에만 업데이트
      if (companyIdFromUrl !== savedCompanyId) {
        console.log(`companyId 업데이트: ${savedCompanyId} -> ${companyIdFromUrl}`);
        localStorage.setItem("companyId", companyIdFromUrl);
      }
      return companyIdFromUrl;
    }
    
    // URL에서 가져온 ID가 비어있는 경우 저장된 ID 사용
    if (savedCompanyId && savedCompanyId !== 'auth') {
      return savedCompanyId;
    }
    
    return '';
  }

  static updateMenuPaths = (menuItems) => {
    // 현재 URL에서 companyId와 projectId 직접 추출
    const url = window.location.href;
    const parsedUrl = new URL(url);
    const pathSegments = parsedUrl.pathname.split("/").filter(segment => segment);
    
    // 로컬 스토리지와 URL에서 가져온 ID 중 URL 우선 적용
    const urlCompanyId = pathSegments.length >= 1 ? pathSegments[0] : '';
    const urlProjectId = (pathSegments.length >= 2 && pathSegments[1] !== 'auth') ? pathSegments[1] : '';
    
    const companyId = urlCompanyId || localStorage.getItem("companyId") || '';
    const projectId = urlProjectId || localStorage.getItem("projectId") || '';
    
    // projectId가 'auth'인 경우 빈 문자열로 처리
    const safeProjectId = projectId === 'auth' ? '' : projectId;
    
    const updatedItems = menuItems.map((item) => {
      // Replace placeholders in the path
      let updatedPath = item.path
        .replace(":companyId", companyId)
        .replace(":projectId", safeProjectId);
      
      // 절대 경로가 아닌 경우 companyId와 projectId 추가
      if (!updatedPath.startsWith('/')) {
        updatedPath = `/${updatedPath}`;
      }
      
      // submenu가 있는 경우 재귀 호출
      if (item.submenu && item.submenu.length > 0) {
        return {
          ...item,
          path: updatedPath,
          submenu: Utils.updateMenuPaths(item.submenu),
        };
      }

      return {
        ...item,
        path: updatedPath,
      };
    });

    return updatedItems;
  };

  static generateTimeArray() {
    const times = [];
    for (let hour = 0; hour < 24; hour++) {
      // Loop over hours from 00 to 23
      for (let minute = 0; minute < 60; minute++) {
        // Loop over minutes from 00 to 59
        // Format the time and push to times array
        times.push(
          `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`
        );
      }
    }
    return times;
  }

  static stripHtmlTags(str) {
    if (typeof str !== 'string') {
      return "";
    }
    return str.replace(/<[^>]*>/g, "");
  }

  static applyLocaleToLink({ locale, link }) {
    return locale !== "zh-Hans"
      ? link.replace(/\/docs\//, `/docs/${locale}/`)
      : link.replace(/\/docs\//, `/docs/en/`);
  }

  static jsonStringToArrayOfObjects(jsonString) {
    const parsedObject = JSON.parse(jsonString);
    return Object.keys(parsedObject).map((key) => {
      return { [key]: parsedObject[key] };
    });
  }

  static getErrorMessages(error) {
    return error?.response?.data?.error;
  }

  static getChatErrorMessages(error) {
    return error?.message;
  }

  static getEditorLanguage(locale) {
    switch (locale) {
      case "zh-Hans":
        return "zh_CN";
      case "ko":
        return "ko_KR";
      case "en":
        return "en_US";
      case "th":
        return "th_TH";
      case "ja":
        return "ja_JP";
      default:
        return locale;
    }
  }

  static isTokenExpired = (token) => {
    try {
      const payload = token.split(".")[1]; // Get the payload
      const decodedPayload = JSON.parse(window.atob(payload)); // Decode Base64Url and parse JSON
      const currentTime = Date.now() / 1000; // Convert milliseconds to seconds
      return decodedPayload.exp < currentTime;
    } catch (error) {
      console.error("Failed to decode token", error);
      return false; // Assume the token is invalid if it fails to decode
    }
  };

  static getLocale() {
    // i18n의 현재 언어 설정 사용
    if (typeof i18n !== 'undefined' && i18n.language) {
      return i18n.language;
    }
    
    // 브라우저의 언어 설정을 사용 (대체 옵션)
    if (typeof navigator !== 'undefined' && navigator.language) {
      return navigator.language;
    }
    
    // Node.js 환경에서는 process.env.LANG을 사용할 수 있습니다
    if (typeof process !== 'undefined' && process.env.LANG) {
      return process.env.LANG.split('.')[0];
    }
    
    // 기본값으로 'en-US' 반환
    return 'en-US';
  }

  /**
   * 현재 언어 설정에 맞는 dayjs 인스턴스를 반환합니다.
   * @param {string|Date|dayjs} date - 포맷할 날짜
   * @param {string} locale - 사용할 로케일 (선택사항)
   * @returns {dayjs} 로케일이 적용된 dayjs 인스턴스
   */
  static formatDateWithLocale(date, locale = null) {
    const currentLocale = locale || this.getLocale();
    const instance = dayjs(date);
    
    switch(currentLocale) {
      case 'ko':
        return instance.locale('ko');
      case 'ja':
        return instance.locale('ja');
      case 'zh-Hans':
        return instance.locale('zh-cn');
      case 'th':
        return instance.locale('th');
      default:
        return instance.locale('en');
    }
  }

  /**
   * Redux store에서 현재 언어를 가져와서 dayjs 포맷팅을 합니다.
   * @param {string|Date|dayjs} date - 포맷할 날짜
   * @param {string} format - 날짜 포맷 (기본값: "YYYY-MM-DD h:mma")
   * @param {object} store - Redux store 인스턴스 (선택사항)
   * @returns {string} 포맷된 날짜 문자열
   */
  static formatDateWithCurrentLocale(date, format = "YYYY-MM-DD h:mma", store = null) {
    let locale = 'en';
    
    // Redux store에서 현재 언어 가져오기
    if (store && store.getState) {
      locale = store.getState().theme.locale;
    } else {
      // i18n에서 현재 언어 가져오기 (fallback)
      locale = this.getLocale();
    }
    
    return this.formatDateWithLocale(date, locale).tz().format(format);
  }

  /**
   * 현재 i18n 언어 설정에 따라 다국어 날짜 형식을 적용합니다.
   * @param {string|Date|dayjs} dateString - 포맷할 날짜
   * @param {string} format - 날짜 포맷 (기본값: "YYYY-MM-DD h:mma")
   * @returns {string} 현재 언어에 맞게 포맷된 날짜 문자열
   */
  static getLocalizedDate(dateString, format = 'YYYY-MM-DD h:mma') {
    let localeCode = 'en';
    let dateFormat = format;
    
    // i18n 현재 언어 가져오기
    const currentLanguage = i18n.language;
    
    switch (currentLanguage) {
      case 'ko':
        localeCode = 'ko';
        if (format === 'YYYY-MM-DD h:mma') {
          dateFormat = 'YYYY-MM-DD A h:mm';
        } else if (format === 'll') {
          dateFormat = 'YYYY년 MM월 DD일';
        } else if (format === 'LT') {
          dateFormat = 'A h:mm';
        } else if (format === 'l') {
          dateFormat = 'YYYY.MM.DD';
        } else if (format === 'L') {
          dateFormat = 'YYYY년 MM월 DD일';
        } else if (format === 'LL') {
          dateFormat = 'YYYY년 MM월 DD일';
        }
        break;
      case 'zh-Hans':
        localeCode = 'zh-cn';
        if (format === 'YYYY-MM-DD h:mma') {
          dateFormat = 'YYYY-MM-DD Ah:mm';
        } else if (format === 'll') {
          dateFormat = 'YYYY年MM月DD日';
        } else if (format === 'LT') {
          dateFormat = 'Ah:mm';
        } else if (format === 'l') {
          dateFormat = 'YYYY/MM/DD';
        } else if (format === 'L') {
          dateFormat = 'YYYY年MM月DD日';
        } else if (format === 'LL') {
          dateFormat = 'YYYY年MM月DD日';
        }
        break;
      case 'ja':
        localeCode = 'ja';
        if (format === 'YYYY-MM-DD h:mma') {
          dateFormat = 'YYYY-MM-DD Ah:mm';
        } else if (format === 'll') {
          dateFormat = 'YYYY年MM月DD日';
        } else if (format === 'LT') {
          dateFormat = 'Ah:mm';
        } else if (format === 'l') {
          dateFormat = 'YYYY/MM/DD';
        } else if (format === 'L') {
          dateFormat = 'YYYY年MM月DD日';
        } else if (format === 'LL') {
          dateFormat = 'YYYY年MM月DD日';
        }
        break;
      case 'th':
        localeCode = 'th';
        if (format === 'YYYY-MM-DD h:mma') {
          dateFormat = 'DD/MM/YYYY HH:mm';
        } else if (format === 'll') {
          dateFormat = 'DD MMM YYYY';
        } else if (format === 'LT') {
          dateFormat = 'HH:mm';
        } else if (format === 'l') {
          dateFormat = 'DD/MM/YYYY';
        } else if (format === 'L') {
          dateFormat = 'DD/MM/YYYY';
        } else if (format === 'LL') {
          dateFormat = 'DD MMMM YYYY';
        }
        break;
      case 'en':
      default:
        localeCode = 'en';
        dateFormat = format; // 영어는 기본 dayjs 형식 사용
    }
    
    return dayjs(dateString).tz().locale(localeCode).format(dateFormat);
  }

  static getNumberFormatWithCurrency(currency, number) {
    let result = new Intl.NumberFormat(this.getLocale(), { style: 'currency', currency }).format(number);
    if (isNaN(number)) result = '0';

    return result;
  }

  static defaultProfanityFilter =
    "4r5e,5h1t,5hit,a55,anal,anus,ar5e,arrse,arse,ass,ass-fucker,asses,assfucker,assfukka,asshole,assholes,asswhole,a_s_s,b!tch,b00bs,b17ch,b1tch,ballbag,balls,ballsack,bastard,beastial,beastiality,bellend,bestial,bestiality,bi+ch,biatch,bitch,bitcher,bitchers,bitches,bitchin,bitching,bloody,blow job,blowjob,blowjobs,boiolas,bollock,bollok,boner,boob,boobs,booobs,booooooobs,breasts,buceta,bugger,bum,bunny fucker,butt,butthole,buttmunch,buttplug,c0ck,c0cksucker,carpet muncher,cawk,chink,cipa,cl1t,clit,clitoris,clits,cnut,cock,cock-sucker,cockface,cockhead,cockmunch,cockmuncher,cocks,cocksuck ,cocksucked ,cocksucker,cocksucking,cocksucks ,cocksuka,cocksukka,cok,cokmuncher,coksucka,coon,cox,crap,cum,cummer,cumming,cums,cumshot,cunilingus,cunillingus,cunnilingus,cunt,cuntlick ,cuntlicker ,cuntlicking ,cunts,cyalis,cyberfuc,cyberfuck ,cyberfucked ,cyberfucker,cyberfuckers,cyberfucking ,d1ck,damn,dick,dickhead,dildo,dildos,dink,dinks,dirsa,dlck,dog-fucker,doggin,dogging,donkeyribber,doosh,duche,dyke,ejaculate,ejaculated,ejaculates ,ejaculating ,ejaculatings,ejaculation,ejakulate,f u c k,f u c k e r,f4nny,fag,fagging,faggitt,faggot,faggs,fagot,fagots,fags,fanny,fannyflaps,fannyfucker,fanyy,fatass,fcuk,fcuker,fcuking,feck,fecker,felching,fellate,fellatio,fingerfuck ,fingerfucked ,fingerfucker ,fingerfuckers,fingerfucking ,fingerfucks ,fistfuck,fistfucked ,fistfucker ,fistfuckers ,fistfucking ,fistfuckings ,fistfucks ,flange,fook,fooker,fuck,fucka,fucked,fucker,fuckers,fuckhead,fuckheads,fuckin,fucking,fuckings,fuckingshitmotherfucker,fuckme ,fucks,fuckwhit,fuckwit,fudge packer,fudgepacker,fuk,fuker,fukker,fukkin,fuks,fukwhit,fukwit,fux,fux0r,f_u_c_k,gangbang,gangbanged ,gangbangs ,gaylord,gaysex,goatse,God,god-dam,god-damned,goddamn,goddamned,hardcoresex ,hell,heshe,hoar,hoare,hoer,homo,hore,horniest,horny,hotsex,jack-off ,jackoff,jap,jerk-off ,jism,jiz ,jizm ,jizz,kawk,knob,knobead,knobed,knobend,knobhead,knobjocky,knobjokey,kock,kondum,kondums,kum,kummer,kumming,kums,kunilingus,l3i+ch,l3itch,labia,lmfao,lust,lusting,m0f0,m0fo,m45terbate,ma5terb8,ma5terbate,masochist,master-bate,masterb8,masterbat*,masterbat3,masterbate,masterbation,masterbations,masturbate,mo-fo,mof0,mofo,mothafuck,mothafucka,mothafuckas,mothafuckaz,mothafucked ,mothafucker,mothafuckers,mothafuckin,mothafucking ,mothafuckings,mothafucks,mother fucker,motherfuck,motherfucked,motherfucker,motherfuckers,motherfuckin,motherfucking,motherfuckings,motherfuckka,motherfucks,muff,mutha,muthafecker,muthafuckker,muther,mutherfucker,n1gga,n1gger,nazi,nigg3r,nigg4h,nigga,niggah,niggas,niggaz,nigger,niggers ,nob,nob jokey,nobhead,nobjocky,nobjokey,numbnuts,nutsack,orgasim ,orgasims ,orgasm,orgasms ,p0rn,pawn,pecker,penis,penisfucker,phonesex,phuck,phuk,phuked,phuking,phukked,phukking,phuks,phuq,pigfucker,pimpis,piss,pissed,pisser,pissers,pisses ,pissflaps,pissin ,pissing,pissoff ,poop,porn,porno,pornography,pornos,prick,pricks ,pron,pube,pusse,pussi,pussies,pussy,pussys ,rectum,retard,rimjaw,rimming,s hit,s.o.b.,sadist,schlong,screwing,scroat,scrote,scrotum,semen,sex,sh!+,sh!t,sh1t,shag,shagger,shaggin,shagging,shemale,shi+,shit,shitdick,shite,shited,shitey,shitfuck,shitfull,shithead,shiting,shitings,shits,shitted,shitter,shitters ,shitting,shittings,shitty ,skank,slut,sluts,smegma,smut,snatch,son-of-a-bitch,spac,spunk,s_h_i_t,t1tt1e5,t1tties,teets,teez,testical,testicle,tit,titfuck,tits,titt,tittie5,tittiefucker,titties,tittyfuck,tittywank,titwank,tosser,turd,tw4t,twat,twathead,twatty,twunt,twunter,v14gra,v1gra,vagina,viagra,vulva,w00se,wang,wank,wanker,wanky,whoar,whore,willies,willy,xrated,xxx,개새끼,개쌔끼,개때끼,개세끼,개색끼,개색히,개쉑히,개쉑,개새꺄,개쌔꺄,개때꺄,개색꺄,개새,개쉐,십새,십쉐,씹새꺄,씹쌔꺄,씹때꺄,씹색꺄,씹새,씹쉐,병신새끼,좆나게,좃나게,존나게,젖나게,젓나게,전나게,졸라게,절라게,좆나,좃나,존나,젖나,젓나,전나,졸라,절라,좆만한,좃만한,존만한,젖만한,젓만한,좆마난,좃마난,존마난,젖마난,젓마난,좆같은,좃같은,젖같은,젓같은,좆가튼,좃가튼,,젖가튼,젓가튼,조까튼,저까튼,좆빠지게,좃빠지게,젖빠지게,젓빠지게,조빠지게,저빠지게,좆밥,좃밥,좁밥,젖밥,젓밥,접밥,조빱,저빱,좆까,좃까,젖까,젓까,조까,저까,좆도,좃도,젖도,조또,씨댕,씨뎅,씹탱,씹창,씨발,씨벌,씨불,씨이발,씨이벌,씨이불,씹팔,씹펄,씹풀,씹빡,띠발,띠벌,띠불,띠방,띠바,띠붕,띠밸,띠팔,띠펄,시벌,시팔,시이발,시이벌,시이불,시이바,시이붕,시이부,시이밸,시이팔,시이펄,시이풀,시이빡,쒸발,쒸벌,쒸불,쒸방,쒸바,쒸빡,쒸이발,쒸이벌,쒸이불,쒸이방,쒸이바,쉬발,쉬벌,쉬불,쉬방,쉬바,쉬붕,쉬부,쉬밸,쉬팔,쉬풀,쉬빡,쓰발,쓰벌,쓰불,쓰방,쓰바,쓰붕,쓰부,쓰밸,쓰팔,쓰펄,쓰풀,쓰빡,쓰이발,쓰이벌,쓰이불,까대,까댄,니미럴,니미랄,니미롤,니미룰,니미를,제기럴,제기랄,제기롤,제기룰,니기미,닝기미,니주가리,니미,쌍년,썅년,쌍넌,썅넌,쌍뇬,썅뇬,개년,개넌,개뇬,쌍놈,썅놈,쌍넘,썅넘,개놈,개넘,썅,지랄,지럴,지롤,쥐랄,쥐럴,쥐롤,병신,븅신,빙신,등신,벼엉신,빙시,미친놈,미친넘,미췬놈,미췬넘,미친년,미친뇬,미친넌,미췬년,미췬뇬,미췬넌,개자식,싸가지,싹아지,염병,옘병,니애미,니에미,c팔,개같은년,c8,개같은놈,씝할,새끠,세끠,쉐끼,새뤼,쉐끼,쇄끼,쉐끼,쇅끼,씨키,세키,ㅅㅐ끼,ㅅㅐㄲㅣ,ㅅㅐㄲㅣ,,시발,싀발,스발,스벌,쉬이발,쓰으방,쉬빨,쉬팍,스벌,씁알,씝알,씹알,씝탱,뵹신,붕신,븅싄,뵹싄,병싄,존니,죶나,죨라,죶니,죤니,죤나,죶마난,죶같은,죶가튼,죶빠지게,즤랄,질알,미친,애자,ㅇㅐ자,ㅇㅐㅈㅏ,니엠,엠창,믜친,뮈친,ㅁl친,섹히,색기,색히,병쉰,뱅신,뱅싄,뱅쉰,븽신,병쉰,애쟈,또라이,떠라이,ㅆl팔,ㅆl발,ㄴㅣㅁㅣ,ㄴlㅁl,늬미,니믜,쓰팍,씌팔,씌발,싀팔,ㅆ1팔,ㅆ1발,씹8,샹년,상년,씹년,씹뇬,챵녀,챵년,창뇬,챵뇬,잡뇬,잡년,계같은뇬,개같은뇬,개섹,죳나,니앰,앰창,애미,씹땡,씹넘,씝년,ㄱH뇬,샹놈,샹늠,개색,개자슥,게세끼,씨입년,씨입뇬,씌벌,싀방,싀봉,씌방,ㅆI발,ㅅ1발,ㅆl발,ㅆi발,ㄴ1ㅇH미,쥐랄,ㅈl랄,뷩신,뱅신,세꺄,미칀,호로자슥애,젠장,제길,젱장,우라질,제긜,젝일,바부,듕신,새키,씨벨,시벵,시펄,씹쌔,등쉬,늬귀미,조낸,씨밝,개세,개샛끼,개쉬끼,개세끼,개쓰렉기,쉬키,색갸,세캬,식키,시키,새캬,듕시나,똘아이,똘추,ㅁ1친,ㅁ1친년,뮈췬,뮈친년,미췐,미췐년,미튄,및친,벵신,병시나,병시,찐따,뷩시,새뀌,색귀,셥새,쉽년,쉽새,시바라,쉽팔,시밝,시벨,십샹,ㅆ│발,쒸뱅,씌파,씌빨,씌밸,씌바,쒸박,씨뱅,c발,g랄,zl랄,凸,미워,뉘미,뉘귀미,^^ㅣ발,ㄱㅐ,ㅅㅐㄲㅣ,ㅅㅐㄱㄱㅣ,ㅅㅐ끼,ㄱH,ㅅㅔㄲㅣ,ㅅHㄲI,ㅅH끼,ㅅㅔㄲ,ㅅ1팔,ㅆㅣ불,ㅆㅂ,ㅅㅂ,씹발,개`,개쇄,씨`발,새'꺄,새'끼,색끼,섀캬,샹뇬,쇄키,싯팔,개씹,섀끼,쌔기,ㅁㅣ친,ㅁl친,망할,씨박,씨'발,씨벵,씨빨,씨파,씨브랄,씨팍,씹빨,씹세,씹쌍,ㅈ1랄,ㅆㅣ발,ㅆㅣ방,ㅆㅣ팔,ㅆㅣ벌,ㅆ!발,ㅆㅣ발,쒸벨,등쉰,똘츄,ㄴㅣ미,ㄴㅣㄱㅣ,ㄴ1ㄱ1,ㄴ1ㅁ1,ㄴ1미,ㄴ1ㅇㅐ,ㄴ1애,니M창,니OH미,뷩신,뷩쉰,븅쉰,빙쉰,시팍,시빨,시파,시밸,쉽뇬,개련,ㅆ1밸,ㅆ1벨,호구,씌벨,애좌,씻팔,ㅆ1바,개샛히애,색휘,싯끼,섹키,쓉년,쓉창,호로자식애,씌뱅,씨팰,씌댕,씌뎅,씹쒜,엠병,앰병,후레자식애,후레자슥애,호로년,후레년,씹빠,뻑큐,빠큐,ㅃr큐,개섀끼,개샹련,니뮈럴,늬기미,앰창,엠창,쓰박,씨부랄,씹샹,연병,시밟,뒤져,짜져,꺼져,잡놈,뒤질래,디질래,아갈,닥쳐,허접,흐접,찌질이,낙태아,다방년,호모,엿먹어,개자지,개보지,애비자지,애미보지,자지,잠지,보지,섹스,공알,좆,좇,좃,젖,빨통,딸딸이,딸따리,빠구리,빠굴이,빠굴,빠순이,빠수니,사까시,불알,부랄,번섹,번쌕,번쎅,떡치기,후장,섹스,색스,쎅스,쌕스,쉑스,섹쓰,색쓰,쎅쓰,쌕쓰,쉑쓰,창녀,창년,갈보,개싑창,죷,죳,좆나,좇나,존나,죤나,욧나,쟈지,죠또,죵나,자쥣,죠빠,쫓까,죡쳐,보쥐,쟘쥐,꼬추,꼬츄,강간,걸레년,곧휴,쟘지,ㅈㅏㅈㅣ,ㅈㅏ지,자쥐,잠쥐,쟘쥐,보'지,섹'스,유두,유방,차앙년,창ㄴㅕ,창뇬,촹년,촹뇬,폰섹,ㅃㅏ구리,ㅃr굴,고츄,붕알,폰색,죠까,porno,dildo,pussy,shit,bitch,anal,fetish,gay,lesbien,cunt,damn,귀두,뽕알";

  static isNaver = process.env.REACT_APP_SOURCE === "NAVER";

  // 번역 API 호출 함수
  static translateText = async (text, source, target, projectId) => {
    try {
      // Apollo Client 사용하여 GraphQL 뮤테이션 요청
      const { data } = await apolloClient.mutate({
        mutation: TRANSLATION_MUTATION,
        variables: {
          text,
          source,
          target,
          projectId
        }
      });
      
      if (data?.translation?.papago) {
        const papagoResponse = JSON.parse(data.translation.papago);
        if (papagoResponse?.status === 1 && papagoResponse?.data?.message?.result?.translatedText) {
          return papagoResponse.data.message.result.translatedText;
        }
      }
      throw new Error('번역 실패');
    } catch (error) {
      console.error('번역 중 오류 발생:', error);
      throw error;
    }
  };

  // 프로젝트 기본 언어 코드 가져오기
  static getProjectDefaultLanguage = () => {
    // 로컬스토리지나 프로젝트 설정에서 기본 언어를 가져오는 로직
    // 실제 구현은 프로젝트 상황에 맞게 수정해야 함
    const defaultLang = localStorage.getItem("projectDefaultLanguage") || i18n.language;
    
    // 일반적인 언어 코드 변환
    switch (defaultLang) {
      case "ko-KR":
      case "ko_KR":
        return "ko";
      case "en-US":
      case "en_US":
        return "en";
      case "ja-JP":
      case "ja_JP":
        return "ja";
      case "zh-CN":
      case "zh_CN":
        return "zh-CN";
      case "zh-TW":
      case "zh_TW":
        return "zh-TW";
      default:
        // 언어 코드가 이미 간단한 형식(ko, en, ja 등)인 경우
        if (defaultLang.length <= 2) {
          return defaultLang;
        }
        // 기본값은 영어
        return "en";
    }
  };

  // 에디터 내용 번역 함수
  static translateEditorContent = async (editor, targetLang) => {
    try {
      const content = editor.getContent();
      
      if (!content.trim()) {
        return;
      }
      
      // 이미 번역 상태인지 확인
      if (!editor.originalContent) {
        // 원본 내용 저장
        editor.originalContent = {
          html: content,
          lang: editor.translateSourceLang || this.getProjectDefaultLanguage() // 프로젝트 기본 언어 사용
        };
      }
      
      const projectId = this.getProjectId();
      const sourceLang = editor.translateSourceLang || editor.originalContent?.lang || this.getProjectDefaultLanguage();
      
      // 같은 언어로 번역 요청하는 경우 무시
      if (sourceLang === targetLang) {
        return;
      }
      
      // 번역 중임을 표시
      editor.setProgressState(true);
      editor.isTranslating = true; // 번역 중 상태 설정
      
      try {
        // HTML 구조를 분석하여 텍스트 노드만 추출
        const dom = new DOMParser().parseFromString(content, 'text/html');
        const textNodes = [];
        
        // 텍스트 노드 추출 함수
        const extractTextNodes = (node) => {
          if (node.nodeType === 3 && node.textContent.trim()) { // 텍스트 노드이고 내용이 있는 경우
            textNodes.push({
              node,
              text: node.textContent.trim()
            });
          } else {
            node.childNodes.forEach(child => extractTextNodes(child));
          }
        };
        
        // 본문에서 텍스트 노드 추출
        extractTextNodes(dom.body);
        
        // 텍스트 노드가 있는 경우에만 번역 진행
        if (textNodes.length > 0) {
          // 텍스트 노드의 내용만 추출하여 배열로 만듦
          const textsToTranslate = textNodes.map(item => item.text);
          
          // 모든 텍스트를 하나의 문자열로 합쳐서 번역 (구분자 사용)
          const combinedText = textsToTranslate.join('\n[SPLIT]\n');
          
          // 번역 API 호출
          const translatedText = await this.translateText(combinedText, sourceLang, targetLang, projectId);
          
          // 번역된 텍스트를 구분자로 다시 나눔
          const translatedTexts = translatedText.split('\n[SPLIT]\n');
          
          // 각 텍스트 노드에 번역된 텍스트 적용
          if (translatedTexts.length === textNodes.length) {
            textNodes.forEach((textNode, index) => {
              textNode.node.textContent = translatedTexts[index];
            });
            
            // 변경된 DOM을 HTML 문자열로 변환
            const serializer = new XMLSerializer();
            let translatedHTML = serializer.serializeToString(dom.body);
            
            // body 태그 제거하고 내부 내용만 반환
            translatedHTML = translatedHTML
              .replace(/<body[^>]*>/i, '')
              .replace(/<\/body>/i, '');
            
            // 현재 번역 언어 저장
            editor.translateSourceLang = targetLang;
            
            // 번역된 내용으로 에디터 업데이트
            editor.setContent(translatedHTML);
          } else {
            // 노드 수와 번역된 텍스트 수가 일치하지 않는 경우 예외 처리
            throw new Error('번역된 텍스트 수와 노드 수가 일치하지 않습니다.');
          }
        } else {
          // 텍스트 노드가 없는 경우 원본 내용 유지
          editor.translateSourceLang = targetLang;
        }
      } catch (parseError) {
        console.error('HTML 파싱 또는 번역 처리 중 오류:', parseError);
        // HTML 파싱 오류 시 기존 방식으로 폴백
        const plainText = this.stripHtmlTags(content);
        if (plainText.trim()) {
          const translatedText = await this.translateText(plainText, sourceLang, targetLang, projectId);
          editor.translateSourceLang = targetLang;
          editor.setContent(translatedText);
        }
      }
      
      // 번역 완료 표시
      editor.setProgressState(false);
      editor.isTranslating = false; // 번역 중 상태 해제
    } catch (error) {
      console.error('번역 중 오류 발생:', error);
      editor.setProgressState(false);
      editor.isTranslating = false; // 에러 발생 시에도 번역 중 상태 해제
      // 에러 메시지 표시 로직 추가 가능
      if (editor.notificationManager) {
        editor.notificationManager.open({
          text: i18n.t('Translation failed'),
          type: 'error'
        });
      }
    }
  };

  // 원본 내용 복원 함수
  static restoreOriginalContent = (editor) => {
    if (editor.originalContent) {
      editor.setContent(editor.originalContent.html);
      editor.translateSourceLang = editor.originalContent.lang;
      // 원본 복원 후 상태 초기화
      editor.originalContent = null;
    }
  };
}

export default Utils;
